import { keepPreviousData } from '@tanstack/react-query';
import { Box, Spinner, Text } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import type { EmailTemplateCopy } from '@duke-hq/email-templates';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { pxToRem } from '@duke-hq/ui/constants/scalable';

/**
 * The email as it will arrive. Rendered on the server by the same function the
 * sender calls, so the preview cannot show an email the sender would not produce.
 * Sandboxed, so its styles cannot reach the page and nothing in it runs.
 */
export function TemplatePreview({
  templateName,
  /** Wording being edited. Absent means show what is stored. */
  draft,
}: {
  templateName: string;
  draft?: EmailTemplateCopy;
}): React.ReactElement {
  const { data, isLoading } = trpcAdmin.emailTemplates.preview.useQuery(
    { templateName, draft },
    // Each draft is a new query, so without this the preview blanks to a spinner
    // every time the author stops typing.
    { placeholderData: keepPreviousData },
  );

  if (isLoading) {
    return (
      <Box p={4}>
        <Spinner />
      </Box>
    );
  }

  if (data == null) {
    return (
      <Text fontSize="sm" color="fg.muted">
        <Trans>This email could not be previewed.</Trans>
      </Text>
    );
  }

  return (
    <Box
      as="iframe"
      title={t`Email preview`}
      sandbox=""
      srcDoc={data.html}
      width="100%"
      height={pxToRem(720)}
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      bg="white"
    />
  );
}
